import { Component } from '@angular/core';
import {
  Client,
  PageInfo,
  PendingMeterDataOut,
  PendingMeterGetAllDataIn,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { SearchComponent } from '../../core/components/search/search.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ClipboardService } from '../../../services/clipboard.service';
import { processDateRange } from '../../../helper/date-utils';
import { ConfirmDialogComponent } from '../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-unconfigured-meters',
  standalone: true,
  imports: [
    TranslateModule,
    SearchComponent,
    DatepickerComponent,
    PaginationComponent,
    SortComponent,
    CommonModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './unconfigured-meters.component.html',
  styleUrl: './unconfigured-meters.component.scss',
})
export class UnconfiguredMetersComponent {
  public currentPage: number = 1;
  public numberOfDevices: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  data: PendingMeterDataOut[] | undefined = [];
  selectedRow: PendingMeterDataOut | null = null;
  public PermissionsEnum = PermissionsEnum;

  isDatepickerOpen = false;
  isModalOpen = false;

  requestData = new PendingMeterGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    public toastr: ToastrService,
    public authService: AuthService,
    private router: Router,
    public clipboardService: ClipboardService
  ) {
    this.reloadTable(1);
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.query3(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfDevices = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfDevices / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  handleRowClick(row: PendingMeterDataOut) {
    if (!this.isModalOpen) {
      this.router.navigate(['/device-setup'], {
        state: { data: row },
      });
    }
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client.query3(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfDevices = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfDevices / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  handleDelete(row: PendingMeterDataOut) {
    this.selectedRow = row;
    this.isModalOpen = true;
  }

  confirmDelete(row: PendingMeterDataOut) {
    this.client.deleteDevice2(row.id).subscribe({
      next: () => {
        this.toastr.success('Device successfully deleted.');
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.isModalOpen = false;
    this.selectedRow = null;
  }

  cancelDelete() {
    this.isModalOpen = false;
    this.selectedRow = null;
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }
}
